<template>
  <div v-if="states.length" class="mb-4 flex flex-col gap-2">
    <div
      v-for="state in states"
      :key="state.id"
      class="flex gap-3 rounded-md bg-white p-4"
      :class="
        state.actions.length === 1
          ? 'flex-col items-start md:flex-row md:items-center md:justify-between'
          : 'items-center justify-between'
      "
    >
      <p class="min-w-0 text-sm font-medium">
        {{ getStateLabel(state)
        }}<template v-if="state.bookingNumber"
          >, {{ `${t('booking')} `
          }}<router-link
            v-if="state.bookingId"
            :to="{ name: 'bookingEdit', params: { id: state.bookingId } }"
            class="text-link"
            >{{ state.bookingNumber }}</router-link
          ><template v-else>{{ state.bookingNumber }}</template></template
        ><template v-if="getStateDates(state)">{{ ` (${getStateDates(state)})` }}</template>
      </p>
      <CVButton
        v-if="state.actions.length === 1"
        class="shrink-0"
        variant="default"
        size="sm"
        :to="state.actions[0].to"
      >
        {{ t(state.actions[0].label) }}
      </CVButton>
      <DropdownDotted v-else class="shrink-0" :buttons="getMenuItems(state.actions)" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { AllocationStatus, AllocationType, FindAllAllocationsSort } from '@carvia/ros-client-types';
import type { Car } from '@/entities/cars/car.entity';
import { Order } from '@/entities/pagination/order.enum';
import { formatDate } from '@/hooks/use-formatted-date';
import { getDateFormat } from '@/hooks/use-date-format';
import { useMinimalAllocations } from '@/queries/use-allocations';
import CVButton from '@/components/buttons/CVButton.vue';
import DropdownDotted from '@/components/DropdownDotted.vue';
import type { DropdownItem } from '@/components/Dropdown.vue';
import {
  getCarOperationalStates,
  type CarOperationalAction,
  type CarOperationalState,
} from './car-operational-states';

const props = defineProps<{
  car: Car;
}>();

const { t } = useI18n();
const router = useRouter();
const now = new Date();

const carId = computed(() => props.car.id);

const { data: currentAllocations } = useMinimalAllocations(
  computed(() => ({
    limit: 20,
    filter: [
      {
        carIds: [carId.value],
        isActive: true,
        startDateBefore: now,
        endDateAfter: now,
      },
      {
        carIds: [carId.value],
        isActive: true,
        status: [AllocationStatus.HANDED_OVER],
      },
    ],
  })),
);

const { data: returnedAllocation } = useMinimalAllocations(
  computed(() => ({
    limit: 1,
    order: Order.DESC,
    sort: [FindAllAllocationsSort.END_DATE],
    filter: [
      {
        carIds: [carId.value],
        status: [AllocationStatus.RETURNED],
        types: [AllocationType.BOOKING, AllocationType.PARTNER_BOOKING],
      },
    ],
  })),
);

const { data: nextBooking } = useMinimalAllocations(
  computed(() => ({
    limit: 1,
    order: Order.ASC,
    sort: [FindAllAllocationsSort.START_DATE],
    filter: [
      {
        carIds: [carId.value],
        isActive: true,
        startDateAfter: now,
        status: [AllocationStatus.CONFIRMED, AllocationStatus.NOT_CONFIRMED],
        types: [AllocationType.BOOKING, AllocationType.PARTNER_BOOKING],
      },
    ],
  })),
);

const { data: nextAllocation } = useMinimalAllocations(
  computed(() => ({
    limit: 1,
    order: Order.ASC,
    sort: [FindAllAllocationsSort.START_DATE],
    filter: [
      {
        carIds: [carId.value],
        isActive: true,
        startDateAfter: now,
        status: [AllocationStatus.CONFIRMED, AllocationStatus.NOT_CONFIRMED],
      },
    ],
  })),
);

const states = computed(() =>
  getCarOperationalStates({
    car: {
      id: props.car.id,
      status: props.car.status,
      readyToHandover: props.car.readyToHandover,
    },
    allocations: [
      ...(currentAllocations.value?.items ?? []),
      ...(returnedAllocation.value?.items ?? []),
      ...(nextAllocation.value?.items ?? []),
      ...(nextBooking.value?.items ?? []),
    ],
    now,
  }),
);

const formatDay = (value?: string) => (value ? formatDate(value, getDateFormat()) : '');

const getStateLabel = (state: CarOperationalState) => {
  if (state.label === 'notPrepared') {
    return state.returnDate
      ? t('notPrepared', { date: formatDay(state.returnDate) })
      : t('notPreparedWithoutDate');
  }
  return t(state.label);
};

const getStateDates = (state: CarOperationalState) => {
  if (state.label === 'activeRental') {
    const from = formatDay(state.date);
    const to = formatDay(state.endDate);
    return from && to ? `${from} – ${to}` : '';
  }
  if (state.label === 'handoverPending' || state.label === 'readyForHandover') {
    return formatDay(state.date);
  }
  return '';
};

const getMenuItems = (actions: CarOperationalAction[]): DropdownItem[] =>
  actions.map((action) => ({
    title: t(action.label),
    onClick: () => router.push(action.to),
  }));
</script>

<i18n lang="json">
{
  "en": {
    "booking": "Booking",
    "activeRental": "In an active rental",
    "handoverPending": "Handover pending",
    "notPrepared": "Not yet prepared after the return on {date}",
    "notPreparedWithoutDate": "Not yet prepared",
    "inWorkshop": "In the workshop",
    "readyForHandover": "Ready for handover",
    "toBooking": "To the booking",
    "toReturn": "To the return",
    "toHandover": "To the handover",
    "toTransfer": "To the transfer",
    "toPartnerRent": "To the partner rental",
    "toPreparation": "To preparation",
    "toWorkshop": "To the workshop appointment",
    "toNextHandover": "To the next handover",
    "toNextBooking": "To the next booking",
    "updateCarStatus": "Update vehicle status"
  },
  "de": {
    "booking": "Buchung",
    "activeRental": "In aktiver Miete",
    "handoverPending": "Übergabe ausstehend",
    "notPrepared": "Noch nicht aufbereitet nach Rückgabe vom {date}",
    "notPreparedWithoutDate": "Noch nicht aufbereitet",
    "inWorkshop": "In einem Werkstattaufenthalt",
    "readyForHandover": "Ready für Übergabe",
    "toBooking": "Zur Buchung",
    "toReturn": "Zur Rückgabe",
    "toHandover": "Zur Übergabe",
    "toTransfer": "Zum Transfer",
    "toPartnerRent": "Zur Partner-Miete",
    "toPreparation": "Zur Aufbereitung",
    "toWorkshop": "Zum Werkstatttermin",
    "toNextHandover": "Zur nächsten Übergabe",
    "toNextBooking": "Zur nächsten Buchung",
    "updateCarStatus": "Status Fahrzeug aktualisieren"
  },
  "es": {
    "booking": "Reserva",
    "activeRental": "En alquiler activo",
    "handoverPending": "Entrega pendiente",
    "notPrepared": "Aún no preparado tras la devolución del {date}",
    "notPreparedWithoutDate": "Aún no preparado",
    "inWorkshop": "En el taller",
    "readyForHandover": "Listo para la entrega",
    "toBooking": "A la reserva",
    "toReturn": "A la devolución",
    "toHandover": "A la entrega",
    "toTransfer": "Al traslado",
    "toPartnerRent": "Al alquiler de partner",
    "toPreparation": "A la preparación",
    "toWorkshop": "A la cita de taller",
    "toNextHandover": "A la próxima entrega",
    "toNextBooking": "A la próxima reserva",
    "updateCarStatus": "Actualizar estado del vehículo"
  }
}
</i18n>
